import { api } from "../api/client";
import { useAsync } from "../lib/useAsync";
import type { SourceHealth } from "../api/types";

const HEALTH_LABEL: Record<SourceHealth["health"], string> = {
  ok: "✅ ok",
  empty: "⚠️ 0 meets",
  error: "⛔ error",
  disabled: "⏸ disabled",
  never: "• never run",
};

function healthClass(h: SourceHealth["health"]): string {
  if (h === "error") return "badge cancelled";
  if (h === "empty" || h === "never") return "badge";
  return "badge going";
}

/**
 * Admin-only view of the discovery source registry: which sites are crawled,
 * when they last ran, the result, and how many upcoming meets each currently
 * contributes. Surfaces broken/empty sources that would otherwise fail silently.
 */
export function DiscoverySources() {
  const { data, loading, error, reload } = useAsync(() => api.listSources(), []);

  return (
    <div>
      <h1>Discovery sources</h1>
      <p className="muted">
        Health of every site the weekly crawler reviews. Fix or disable sources
        showing errors or 0 meets. <a href="/discovery/review">Review queue →</a>
      </p>

      {loading && <p>Loading…</p>}
      {error && <p className="error">{error}</p>}

      {data && (
        <div className="card">
          <div className="row" style={{ gap: "0.75rem", flexWrap: "wrap" }}>
            <span className="badge going">{data.summary.ok} ok</span>
            <span className="badge">{data.summary.empty} empty</span>
            <span className="badge cancelled">{data.summary.error} error</span>
            <span className="badge">{data.summary.disabled} disabled</span>
            <span className="muted">
              Last crawl:{" "}
              {data.summary.lastCrawlAt
                ? new Date(data.summary.lastCrawlAt).toLocaleString()
                : "never"}
            </span>
            <button className="secondary" onClick={reload} style={{ marginLeft: "auto" }}>
              Refresh
            </button>
          </div>
        </div>
      )}

      {data?.items.map((s) => (
        <div key={s.sourceId} className="card">
          <div className="row spread">
            <span>
              <strong>{s.name}</strong>{" "}
              <span className="muted">
                · {s.state} · {s.adapter}
              </span>
            </span>
            <span className="row" style={{ gap: "0.4rem" }}>
              <span className="badge">{s.publishedUpcoming} upcoming</span>
              <span className={healthClass(s.health)}>
                {HEALTH_LABEL[s.health]}
              </span>
            </span>
          </div>
          <p className="muted" style={{ marginTop: "0.4rem" }}>
            {s.lastResult ?? "not yet crawled"}
            {s.lastCrawledAt && (
              <> · last run {new Date(s.lastCrawledAt).toLocaleString()}</>
            )}
          </p>
          <a href={s.url} target="_blank" rel="noreferrer" className="muted">
            {s.url}
          </a>
        </div>
      ))}
      {data && data.items.length === 0 && (
        <p className="muted">No sources registered yet.</p>
      )}
    </div>
  );
}
